import React from 'react';
import { useOnboardingViewport } from './shared/useViewport';

const OnboardingLayout = ({
  children,
  progress,
  onBack,
  onNext,
  nextLabel = "Continue",
  showBack = true,
  showNext = true,
  fullScreen = false,
  theme = 'light',
  user,
  handleLogout,
  rightActions
}) => {
  const vp = useOnboardingViewport();
  const isDark = theme === 'dark';
  
  const colors = {
    background: isDark
      ? "linear-gradient(180deg, #0F172A 0%, #1E293B 100%)"
      : "linear-gradient(180deg, #E6FFFA 0%, #F0FDFB 50%, #FFFFFF 100%)",
    surface: isDark ? '#1E293B' : '#FFFFFF',
    text: isDark ? '#F1F5F9' : '#0F172A',
    secondaryText: isDark ? '#94A3B8' : '#64748B',
    accent: '#14B8A6',
    border: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)'
  };

  const primaryCTAStyle = {
    width: '100%',
    height: `${vp.spacing.ctaHeight}px`,
    borderRadius: `${vp.spacing.ctaBorderRadius}px`,
    background: colors.accent,
    color: 'white',
    fontSize: `${vp.spacing.ctaFontSize}px`,
    fontWeight: '800',
    border: 'none',
    cursor: 'pointer',
    boxShadow: '0 8px 16px rgba(20, 184, 166, 0.2)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '10px',
    transition: 'transform 0.2s'
  };

  // Updated: content area now has top padding based on viewport class to avoid "clinging" to the edge
  const contentTopPadding = vp.spacing.headerPaddingTop;
  const contentBottomPadding = vp.spacing.footerPaddingBottom + vp.spacing.ctaPaddingBottom;

  return (
    <div style={{
      width: '100%',
      minHeight: '100dvh',
      background: colors.background,
      color: colors.text,
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
      position: 'relative',
      fontFamily: "'Inter', sans-serif",
      overflowX: 'hidden'
    }}>
      {user?.id && (
        <div style={{
          position: "absolute",
          top: "16px",
          right: "16px",
          display: "flex",
          alignItems: "center",
          gap: "4px",
          zIndex: 101,
          pointerEvents: "auto"
        }}>
          {rightActions}
          <button
            onClick={handleLogout}
            style={{
              background: "none",
              border: "none",
              color: "#94A3B8",
              fontSize: "13px",
              fontWeight: 500,
              cursor: "pointer",
              padding: "8px"
            }}
          >
            Logout
          </button>
        </div>
      )}
      {/* Progress Bar */}
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        height: '4px',
        background: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
        zIndex: 1000
      }}>
        <div style={{
          width: `${progress * 100}%`,
          height: '100%',
          background: `linear-gradient(90deg, ${colors.accent}, #2DD4BF)`,
          transition: 'width 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
          boxShadow: `0 0 10px ${colors.accent}40`
        }} />
      </div>

      {/* Header / Nav */}
      {showBack && (
        <div style={{
          padding: `${contentTopPadding}px ${vp.spacing.screenPaddingH}px 12px`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          zIndex: 100
        }}>
          <button
            onClick={onBack}
            style={{
              background: 'transparent',
              border: 'none',
              padding: '8px',
              cursor: 'pointer',
              color: colors.secondaryText,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '12px',
              marginLeft: '-8px'
            }}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M15 18l-6-6 6-6" />
            </svg>
          </button>
          <div style={{ width: 40 }} />
        </div>
      )}

      {/* Content Area – now uniformly padded */}
      <div style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        padding: `${contentTopPadding}px ${vp.spacing.screenPaddingH}px ${contentBottomPadding}px`,
        position: 'relative',
        zIndex: 10,
        animation: 'fadeIn 0.5s ease-out'
      }}>
        {children}
      </div>

      {/* Fixed Footer CTA */}
      {showNext && (
        <div style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          padding: `20px ${vp.spacing.screenPaddingH}px calc(20px + env(safe-area-inset-bottom))`,
          background: `linear-gradient(0deg, ${isDark ? '#1E293B' : '#FFFFFF'} 80%, transparent)`,
          zIndex: 100,
          display: 'flex',
          flexDirection: 'column',
          gap: '12px'
        }}>
          <button
            onClick={onNext}
            style={primaryCTAStyle}
          >
            {nextLabel}
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
              <path d="M5 12h14M12 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      )}

      <style>{`
        @keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
        button:active { transform: scale(0.98); }
      `}</style>
    </div>
  );
};

export default OnboardingLayout;
